/**
 * Что из урока действительно нужно отправить на сервер.
 *
 * ⚠️ ЗАЧЕМ. Конструктор при каждом сохранении слал `PUT` на ВСЕ блоки урока подряд,
 * менялись они или нет (техдолг п. 178). Урок на 40 блоков — 40 запросов на каждое
 * автосохранение, то есть раз в 2,5 с правки, и каждый из них ещё будит проход зеркала
 * подтипов. Теперь блок отправляется, только если его снимок разошёлся с тем, что уже
 * лежит на сервере.
 *
 * ⚠️ СРАВНЕНИЕ ПО СМЫСЛУ JSON, А НЕ ПОБАЙТНО — как `одинаковоеСодержимое` в
 * `services/courses-service/internal/service/block_content_guard.go:251`. Порядок ключей
 * меняют и редактор, и `jsonb` в базе; побайтное сравнение считало бы правкой любую
 * перестановку, и смысл затеи пропал бы.
 *
 * ⚠️ ЦЕНА ОШИБКИ В ОБЕ СТОРОНЫ:
 *   — снимок совпал ошибочно → правка методиста МОЛЧА не уедет, а на экране «Сохранено»;
 *   — снимок разошёлся ошибочно → вернулись к 40 запросам, вреда нет.
 * Поэтому при любом сомнении (значение, которое не сериализуется) снимок считается
 * разошедшимся: лишний `PUT` дешевле потерянной правки.
 */

/** Порядок ключей не важен: объект раскладывается по отсортированным ключам. */
function stable(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(stable);
  if (v && typeof v === 'object') {
    const src = v as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(src).sort()) out[k] = stable(src[k]);
    return out;
  }
  return v;
}

/** Всё, что уходит в `createBlock` / `updateBlock`. Разошлось любое поле — блок правился. */
export type BlockShape = { type: string; content: unknown; order_index: number };

/** Снимок раздела: `updateSection` шлёт заголовок, место и признак домашней работы. */
export type SectionShape = { title: string; order_index: number; is_homework: boolean };

/**
 * Снимок для сравнения. `null` — снимок снять не удалось (значение не сериализуется:
 * функция, циклическая ссылка, `BigInt`); такой блок отправляется всегда.
 */
export function snapshot(shape: BlockShape | SectionShape): string | null {
  try {
    return JSON.stringify(stable(shape));
  } catch {
    return null;
  }
}

/**
 * Отправлять ли узел. `saved` — снимок, сделанный при последней удачной записи или при
 * загрузке урока с сервера; его отсутствие означает «про этот узел мы ничего не знаем».
 */
export function needsSave(current: string | null, saved: string | null | undefined): boolean {
  return current === null || saved === null || saved === undefined || current !== saved;
}
